import { ReactNode, useEffect, useRef, useState } from 'react';
import { Check, X, Pen } from 'lucide-react';
import {
	Input,
	Textarea,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@hiring-intelligence/ui';

interface BaseProps {
	label: string;
	value: string | null | undefined;
	onSave: (value: string) => void;
	disabled?: boolean;
	placeholder?: string;
	/** Render the displayed value when not editing — overrides default text rendering. */
	renderDisplay?: (value: string | null | undefined) => ReactNode;
}

interface TextFieldProps extends BaseProps {
	type?: 'text' | 'email' | 'tel' | 'url' | 'date';
}

/**
 * HubSpot-style click-to-edit text/email/phone field.
 * Shows label + value. Click to edit. Enter to save, Esc to cancel.
 */
export function EditableTextField({ label, value, onSave, disabled, placeholder, type = 'text', renderDisplay }: TextFieldProps) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value || '');
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => { if (editing) setDraft(value || ''); }, [editing, value]);
	useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

	const commit = () => {
		if (draft !== (value || '')) onSave(draft);
		setEditing(false);
	};
	const cancel = () => { setDraft(value || ''); setEditing(false); };

	if (editing) {
		return (
			<FieldShell label={label}>
				<div className="flex items-center gap-1">
					<Input
						ref={inputRef}
						type={type}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') { e.preventDefault(); commit(); }
							if (e.key === 'Escape') { e.preventDefault(); cancel(); }
						}}
						className="h-8 text-sm"
						placeholder={placeholder}
					/>
					<IconBtn aria="Save" onClick={commit}><Check className="h-3.5 w-3.5" /></IconBtn>
					<IconBtn aria="Cancel" onClick={cancel}><X className="h-3.5 w-3.5" /></IconBtn>
				</div>
			</FieldShell>
		);
	}

	return (
		<FieldShell label={label}>
			<DisplayRow disabled={disabled} onClick={() => !disabled && setEditing(true)}>
				{renderDisplay ? renderDisplay(value) : (
					value ? <span className="text-sm">{value}</span> : <span className="text-sm text-muted-foreground italic">—</span>
				)}
			</DisplayRow>
		</FieldShell>
	);
}

interface TextareaFieldProps extends BaseProps {
	rows?: number;
}
export function EditableTextarea({ label, value, onSave, disabled, placeholder, rows = 3, renderDisplay }: TextareaFieldProps) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value || '');
	const ref = useRef<HTMLTextAreaElement>(null);
	useEffect(() => { if (editing) setDraft(value || ''); }, [editing, value]);
	useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

	if (editing) {
		const commit = () => { if (draft !== (value || '')) onSave(draft); setEditing(false); };
		const cancel = () => { setDraft(value || ''); setEditing(false); };
		return (
			<FieldShell label={label}>
				<Textarea
					ref={ref}
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
					rows={rows}
					className="text-sm"
					placeholder={placeholder}
				/>
				<div className="mt-1 flex justify-end gap-1">
					<IconBtn aria="Save" onClick={commit}><Check className="h-3.5 w-3.5" /></IconBtn>
					<IconBtn aria="Cancel" onClick={cancel}><X className="h-3.5 w-3.5" /></IconBtn>
				</div>
			</FieldShell>
		);
	}

	return (
		<FieldShell label={label}>
			<DisplayRow disabled={disabled} onClick={() => !disabled && setEditing(true)}>
				{renderDisplay ? renderDisplay(value) : (
					value ? <span className="text-sm whitespace-pre-wrap break-words">{value}</span> : <span className="text-sm text-muted-foreground italic">—</span>
				)}
			</DisplayRow>
		</FieldShell>
	);
}

interface SelectOption { value: string; label: string }
interface SelectFieldProps extends BaseProps {
	options: SelectOption[];
}
export function EditableSelectField({ label, value, onSave, options, disabled, placeholder, renderDisplay }: SelectFieldProps) {
	const [editing, setEditing] = useState(false);
	const current = options.find((o) => o.value === value);

	if (editing) {
		return (
			<FieldShell label={label}>
				<Select
					defaultValue={value || undefined}
					onValueChange={(v) => { onSave(v); setEditing(false); }}
					open
					onOpenChange={(o) => { if (!o) setEditing(false); }}
				>
					<SelectTrigger className="h-8 text-sm">
						<SelectValue placeholder={placeholder || 'Select...'} />
					</SelectTrigger>
					<SelectContent>
						{options.map((o) => (
							<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
						))}
					</SelectContent>
				</Select>
			</FieldShell>
		);
	}

	return (
		<FieldShell label={label}>
			<DisplayRow disabled={disabled} onClick={() => !disabled && setEditing(true)}>
				{renderDisplay ? renderDisplay(value) : (
					current
						? <span className="text-sm">{current.label}</span>
						: <span className="text-sm text-muted-foreground italic">{placeholder || '—'}</span>
				)}
			</DisplayRow>
		</FieldShell>
	);
}

/** Display-only field (no edit affordance) — for derived values like create date. */
export function ReadOnlyField({ label, children }: { label: string; children: ReactNode }) {
	return (
		<FieldShell label={label}>
			<div className="text-sm py-1 pl-1">{children}</div>
		</FieldShell>
	);
}

// ---- internal helpers ----

function FieldShell({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="space-y-0.5">
			<div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</div>
			{children}
		</div>
	);
}

function DisplayRow({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			className={`group flex w-full min-h-[28px] items-start justify-between gap-2 rounded px-1 py-1 text-left transition-colors ${disabled ? 'cursor-default' : 'hover:bg-muted/60'}`}
		>
			<div className="min-w-0 flex-1">{children}</div>
			{!disabled && <Pen className="mt-1 h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-60" />}
		</button>
	);
}

function IconBtn({ children, onClick, aria }: { children: ReactNode; onClick: () => void; aria: string }) {
	return (
		<button
			type="button"
			aria-label={aria}
			onClick={onClick}
			className="flex h-7 w-7 items-center justify-center rounded border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
		>
			{children}
		</button>
	);
}
